import { Dispatch } from 'redux';
import {
  fetchStatesService,
  searchByAddressService,
  searchByOwnerService,
  fetchDriversService,
  searchByDriversService,
} from './PropertySearch.services';
import {
  GetStatesSuccessType,
  SetResultLoadingType,
  GET_STATES_SUCCESS,
  SET_LOADING,
  GET_DRIVERS_SUCCESS,
  GetDriversSuccessType,
  PROPERTY_SEARCH_RESULT_SUCCESS,
  SearchResultSuccessType,
  RESET_DRIVERS_DATA,
  ResetDriversDataType,
  RESET_RESULTS,
  ResetResultsType,
} from './PropertySearch.types';
import { httpError, json, showWarnNotification, FETCH_HEADER } from 'common';
import {
  searchByAddressBody,
  searchByOwnerBody,
} from './PropertySearch.selectors';
import {
  PROPERTY_SEARCH_FORM_INITIAL_VALUES,
  EMPTY_VALUE,
} from './PropertySearch.constants';
import { PropertySearchResult, GetDriversResult } from './models';
import { USState } from 'common/models';

const setResultLoading = (loading: boolean): SetResultLoadingType => ({
  type: SET_LOADING,
  loading,
});

const getStatesSuccess = (usState: USState): GetStatesSuccessType => ({
  type: GET_STATES_SUCCESS,
  usState,
});

export const getStates =
  (usState: string, bearerToken: string) => (dispatch: Dispatch) => {
    dispatch(setResultLoading(true));
    fetchStatesService(usState, bearerToken)
      .then(httpError(dispatch))
      .then(json)
      .then((response) => {
        if (response.errorMessage) throw Error(response.errorMessage);
        dispatch(getStatesSuccess(response));
        dispatch(setResultLoading(false));
      })
      .catch((error) => {
        dispatch(setResultLoading(false));
        showWarnNotification(`States`, error.message);
      });
  };

const resultSuccess = (
  result: PropertySearchResult,
): SearchResultSuccessType => ({
  type: PROPERTY_SEARCH_RESULT_SUCCESS,
  result,
});

export const handleSearchRequest = (
  service: typeof searchByAddressService | typeof searchByOwnerService,
  body: any,
  bearerToken: string,
  dispatch: Dispatch,
) => {
  dispatch(setResultLoading(true));
  dispatch(resetResults());
  service(body, bearerToken)
    .then(httpError(dispatch))
    .then(json)
    .then((response: any) => {
      if (response.errorMessage) throw Error(response.errorMessage);
      dispatch(resultSuccess(response));
      dispatch(setResultLoading(false));
    })
    .catch((error: any) => {
      dispatch(setResultLoading(false));
      showWarnNotification(FETCH_HEADER, error.message);
    });
};

export const searchByAddress =
  (
    formValues: typeof PROPERTY_SEARCH_FORM_INITIAL_VALUES,
    bearerToken: string,
  ) =>
  (dispatch: Dispatch) => {
    const body = searchByAddressBody(formValues);
    handleSearchRequest(searchByAddressService, body, bearerToken, dispatch);
  };

export const searchByOwner =
  (formValues: any, bearerToken: string) => (dispatch: Dispatch) => {
    const body = searchByOwnerBody(formValues);
    handleSearchRequest(searchByOwnerService, body, bearerToken, dispatch);
  };

export const searchByDrivers =
  (
    driversData: GetDriversResult,
    { driversSearch }: any,
    bearerToken: string,
  ) =>
  (dispatch: Dispatch) => {
    const body = {
      ...driversData,
      drivers: driversData.drivers.map((driver) => ({
        ...driver,
        value: driversSearch[driver.fIndex] || EMPTY_VALUE,
      })),
    };
    handleSearchRequest(searchByDriversService, body, bearerToken, dispatch);
  };

const getDriversSuccess = (
  driversData: GetDriversResult,
): GetDriversSuccessType => ({
  type: GET_DRIVERS_SUCCESS,
  driversData,
});

export const getDrivers =
  (hobCode: string, driversData: GetDriversResult, bearerToken: string) =>
  (dispatch: Dispatch) => {
    dispatch(setResultLoading(true));
    fetchDriversService(hobCode, bearerToken)
      .then(httpError(dispatch))
      .then(json)
      .then((response) => {
        if (response.errorMessage) throw Error(response.errorMessage);
        dispatch(getDriversSuccess(response));
        dispatch(setResultLoading(false));
      })
      .catch((error) => {
        dispatch(setResultLoading(false));
        showWarnNotification(FETCH_HEADER, error.message);
      });
  };

export const resetDriversData = (): ResetDriversDataType => ({
  type: RESET_DRIVERS_DATA,
});

export const resetResults = (): ResetResultsType => ({
  type: RESET_RESULTS,
});
